import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Input, 
  Space, 
  Typography, 
  message,
  Tooltip,
  Badge,
  Spin
} from 'antd';
import { 
  FileTextOutlined, 
  SaveOutlined, 
  CloseOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useTranslation } from '../hooks/useTranslation';
import { useUpdateAppointment } from '../hooks/useGraphQL';

const { TextArea } = Input;
const { Text } = Typography;

interface FloatingCaseNotesProps {
  appointmentId: string;
  initialNotes?: string;
  isVisible: boolean;
  onToggleVisibility: () => void;
  position?: { x: number; y: number };
  onPositionChange?: (position: { x: number; y: number }) => void;
}

const FloatingCaseNotes: React.FC<FloatingCaseNotesProps> = ({
  appointmentId,
  initialNotes = '',
  isVisible,
  onToggleVisibility,
  position = { x: 50, y: window.innerHeight / 2 - 200 },
  onPositionChange
}) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastSavedNotes, setLastSavedNotes] = useState('');

  const [updateAppointment] = useUpdateAppointment();

  // Generate a unique key for localStorage based on appointment ID
  const getStorageKey = (id: string) => `case_notes_${id}`;

  // Load notes from localStorage first, then from server
  useEffect(() => {
    if (appointmentId) {
      setIsLoading(true);
      
      // Try to load from localStorage first
      const storedNotes = localStorage.getItem(getStorageKey(appointmentId));
      
      if (storedNotes) {
        setNotes(storedNotes);
        setLastSavedNotes(storedNotes);
        setHasUnsavedChanges(false);
      }
      
      // If we have server notes and they're different from stored notes, use server notes
      if (initialNotes && initialNotes !== storedNotes) {
        setNotes(initialNotes);
        setLastSavedNotes(initialNotes);
        // Update localStorage with server notes
        localStorage.setItem(getStorageKey(appointmentId), initialNotes);
      }
      
      setIsLoading(false);
    }
  }, [appointmentId, initialNotes]);

  // Update local notes when initialNotes prop changes (e.g., after refetch)
  useEffect(() => {
    if (appointmentId && initialNotes !== undefined) {
      // Only update if the server notes are different from what we have locally
      // and if we don't have unsaved changes
      if (initialNotes !== lastSavedNotes && !hasUnsavedChanges) {
        setNotes(initialNotes);
        setLastSavedNotes(initialNotes);
        localStorage.setItem(getStorageKey(appointmentId), initialNotes);
      }
    }
  }, [appointmentId, initialNotes, lastSavedNotes, hasUnsavedChanges]);

  // Track changes to notes
  useEffect(() => {
    if (!isLoading) {
      setHasUnsavedChanges(notes !== lastSavedNotes);
    }
  }, [notes, lastSavedNotes, isLoading]);

  // Auto-save to localStorage when notes change (debounced)
  useEffect(() => {
    if (!isLoading && appointmentId) {
      const timeoutId = setTimeout(() => {
        localStorage.setItem(getStorageKey(appointmentId), notes);
      }, 1000); // Save to localStorage after 1 second of no typing

      return () => clearTimeout(timeoutId);
    }
  }, [notes, appointmentId, isLoading]);

  // Cleanup localStorage when appointmentId changes or component unmounts
  useEffect(() => {
    return () => {
      // Clean up localStorage for this appointment when component unmounts
      if (appointmentId) {
        // Only remove if we're not saving important data
        // localStorage.removeItem(getStorageKey(appointmentId));
      }
    };
  }, [appointmentId]);

  // Handle click outside to save
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditing && hasUnsavedChanges) {
        const target = event.target as HTMLElement;
        if (!target.closest('.ant-card') && !target.closest('.ant-modal')) {
          handleSave();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing, hasUnsavedChanges]);

  const handleSave = async () => {
    if (!notes.trim()) {
      message.warning('Please enter some notes before saving');
      return;
    }

    setIsSaving(true);
    try {
      await updateAppointment({
        variables: {
          id: appointmentId,
          updateAppointmentDto: {
            doctor_notes: notes.trim()
          }
        }
      });

      // Update localStorage with the saved notes
      localStorage.setItem(getStorageKey(appointmentId), notes.trim());
      setLastSavedNotes(notes.trim());
      setHasUnsavedChanges(false);
      
      message.success(t('agora.caseNotesSaved'));
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save case notes:', error);
      message.error(t('agora.caseNotesSaveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setNotes(lastSavedNotes);
    setIsEditing(false);
    setHasUnsavedChanges(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // Allow dragging from the header area (title bar)
    const target = e.target as HTMLElement;
    if (target.closest('.ant-card-head') || target === e.currentTarget) {
      setIsDragging(true);
      const rect = e.currentTarget.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && onPositionChange) {
      const newX = Math.max(0, Math.min(window.innerWidth - 450, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 400, e.clientY - dragOffset.y));
      onPositionChange({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset, onPositionChange]);

  if (!isVisible) {
    return (
      <Tooltip title={t('agora.caseNotes')}>
        <Button
          type="primary"
          icon={<FileTextOutlined />}
          onClick={onToggleVisibility}
          style={{
            position: 'fixed',
            top: position.y,
            left: position.x,
            zIndex: 1000,
            borderRadius: '50%',
            width: 48,
            height: 48,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Card
      title={
        <div style={{ cursor: 'grab', userSelect: 'none' }}>
          <Space>
            <FileTextOutlined />
            <span>{t('agora.caseNotesTitle')}</span>
            <Badge 
              count={notes.length} 
              showZero 
              style={{ backgroundColor: notes.length > 0 ? '#52c41a' : '#d9d9d9' }}
            />
            {hasUnsavedChanges && (
              <Badge 
                count="*" 
                style={{ 
                  backgroundColor: '#faad14',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
                title="Unsaved changes"
              />
            )}
          </Space>
        </div>
      }
      extra={
        <Space>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onToggleVisibility}
            size="small"
            style={{ color: '#666' }}
            title={t('common.close')}
          />
        </Space>
      }
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        width: 450,
        maxHeight: '80vh',
        zIndex: 1000,
        boxShadow: isDragging 
          ? '0 8px 32px rgba(0,0,0,0.4)' 
          : '0 4px 20px rgba(0,0,0,0.3)',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        transition: isDragging ? 'none' : 'box-shadow 0.2s ease'
      }}
      onMouseDown={handleMouseDown}
      bodyStyle={{ padding: '16px' }}
      headStyle={{ 
        cursor: 'grab',
        backgroundColor: isDragging ? '#f0f0f0' : '#ffffff',
        borderBottom: '2px solid #1890ff'
      }}
    >
              <div style={{ marginBottom: 16 }}>
          {isEditing ? (
            <div>
              <TextArea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('agora.caseNotesPlaceholder')}
                rows={10}
                autoFocus
                style={{ 
                  resize: 'vertical',
                  minHeight: '200px',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  marginBottom: '8px'
                }}
              />
              <div style={{ 
                textAlign: 'right', 
                fontSize: '12px', 
                color: notes.length > 1000 ? '#ff4d4f' : '#8c8c8c' 
              }}>
                {notes.length} characters
              </div>
            </div>
          ) : (
            <div
              style={{
                minHeight: 200,
                padding: '12px 16px',
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                backgroundColor: '#fafafa',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                cursor: 'pointer',
                fontSize: '14px',
                lineHeight: '1.6',
                maxHeight: '400px',
                overflowY: 'auto'
              }}
              onClick={() => setIsEditing(true)}
            >
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: '10px', color: '#8c8c8c' }}>
                    Loading case notes...
                  </div>
                </div>
              ) : (
                notes || (
                  <Text type="secondary" style={{ fontStyle: 'italic' }}>
                    {t('agora.caseNotesPlaceholder')}
                  </Text>
                )
              )}
            </div>
          )}
        </div>

      {isEditing ? (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Drag the header to move • Click outside to save
          </Text>
          <Space>
            <Button size="small" onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
            <Button
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={isSaving}
            >
              {t('common.save')}
            </Button>
          </Space>
        </Space>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => setIsEditing(true)}
            disabled={isLoading}
            style={{ width: '100%' }}
          >
            {isLoading ? 'Loading...' : t('common.edit')}
          </Button>
          <div style={{ 
            marginTop: '8px', 
            fontSize: '11px', 
            color: '#8c8c8c',
            fontStyle: 'italic'
          }}>
            Drag the header to move • Click to edit
          </div>
        </div>
      )}
    </Card>
  );
};

export default FloatingCaseNotes;
